import { AssetRequestError, describeFetchFailure } from './getAssetById';
import { fetchWithDevProxyFallback } from './fetchWithDevProxyFallback';
import { buildAuthorizedHeaders } from './withApiAuth';

export type BaseUrlConnectionResult =
  | {
      ok: true;
      checkedUrl: string;
      httpStatus: number;
      httpStatusText: string;
      wwwAuthenticate?: string;
      responsePreview?: string;
      rejectionHint?: string;
    }
  | { ok: false; error: AssetRequestError };

function makeRejectionHint(status: number, wwwAuthenticate?: string, responsePreview?: string): string | undefined {
  if (status < 400) {
    return undefined;
  }
  const haystack = `${wwwAuthenticate ?? ''}\n${responsePreview ?? ''}`.toLowerCase();
  if (haystack.includes('ip') && (haystack.includes('mismatch') || haystack.includes('not allowed') || haystack.includes('invalid'))) {
    return 'Token appears to be IP-restricted and your current public IP may not match the token claim.';
  }
  if (haystack.includes('insufficient_scope') || haystack.includes('scope')) {
    return 'Token is valid but missing required scope/permission for this endpoint.';
  }
  if (haystack.includes('invalid_token') || haystack.includes('signature') || haystack.includes('issuer')) {
    return 'Token failed validation (invalid token/signature/issuer) on the API side.';
  }
  if (status === 401) {
    return 'Server rejected authentication. Check token audience, signature/issuer, IP binding, and clock skew.';
  }
  if (status === 403) {
    return 'Server authenticated you but denied authorization for this endpoint (role/scope/client type).';
  }
  return `Request denied with HTTP ${status}. Check WWW-Authenticate and response details below.`;
}

/**
 * Verifies the browser can reach `BASE_URL` over the network (TLS + HTTP).
 * Any HTTP status counts as reachable — failures are transport/CORS/DNS errors.
 */
export async function checkBaseUrlConnection(): Promise<BaseUrlConnectionResult> {
  try {
    const { response: res, urlUsed: checkedUrl } = await fetchWithDevProxyFallback('', {
      method: 'GET',
      cache: 'no-store',
      headers: await buildAuthorizedHeaders({ Accept: '*/*' }),
    });
    const responseText = await res.text();
    const trimmed = responseText.trim();
    const responsePreview =
      trimmed.length === 0
        ? undefined
        : trimmed.length > 4_000
          ? `${trimmed.slice(0, 4_000)}\n\n… [truncated: ${trimmed.length} chars total]`
          : trimmed;
    const wwwAuthenticate = res.headers.get('www-authenticate')?.trim() || undefined;

    return {
      ok: true,
      checkedUrl,
      httpStatus: res.status,
      httpStatusText: res.statusText || '',
      wwwAuthenticate,
      responsePreview,
      rejectionHint: makeRejectionHint(res.status, wwwAuthenticate, responsePreview),
    };
  } catch (err) {
    const networkDetails = describeFetchFailure(err);
    return {
      ok: false,
      error: new AssetRequestError({
        message: networkDetails,
        networkDetails,
      }),
    };
  }
}
